import { FC, useEffect } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import dayjs from 'dayjs';

import { TextInput, LocationInput, DateRangeInput, CheckBoxInput } from '@carcareers/components';
import { useForm } from '@carcareers/contexts';

export const AdminPaymentStepForm: FC = () => {
  const { getData, setData } = useForm();
  const isFreePoll = getData('payment.isFree');
  const startDate = getData<string>('startDate');
  const endDate = getData<string>('endDate');
  const postDuration = dayjs(endDate).diff(dayjs(startDate), 'day');

  // Set default total payment due when not free, clear fields when free
  useEffect(() => {
    if (isFreePoll) {
      // Clear payment fields when posting for free
      setData('payment.totalPaymentDue', 0);
      setData('payment.cardNumber', '');
      setData('payment.expirationDate', '');
      setData('payment.cardCode', '');
      setData('payment.billingAddress', '');
    } else if (!getData('payment.totalPaymentDue') || Number(getData('payment.totalPaymentDue')) === 0) {
      // Set default total payment due when not free
      setData('payment.totalPaymentDue', 329.00);
    }
  }, [isFreePoll, getData, setData]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Typography variant="h4">Duration</Typography>
      <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <DateRangeInput nameStart="startDate" nameEnd="endDate" />
        <Typography variant="body1">{postDuration} days</Typography>
      </Box>
      <Divider />
      <Typography variant="h4">Payment Information</Typography>
      <CheckBoxInput label="Post this job for free" name="payment.isFree" />
      {!isFreePoll && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h5">Payment Details</Typography>
          <TextInput
            required={!isFreePoll}
            fullWidth
            label="Total Amount Due"
            name="payment.totalPaymentDue"
            type="number"
            sx={{ borderRadius: 3 }}
            disabled={isFreePoll}
          />
          <TextInput 
            required={!isFreePoll} 
            fullWidth 
            label="Card Number" 
            name="payment.cardNumber" 
            sx={{ borderRadius: 3 }}
            disabled={isFreePoll}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextInput
              required={!isFreePoll}
              fullWidth
              label="Expiration Date"
              name="payment.expirationDate"
              sx={{ borderRadius: 3 }}
              disabled={isFreePoll}
            />
            <TextInput
              required={!isFreePoll}
              fullWidth
              label="Security Code"
              name="payment.cardCode"
              type="password"
              sx={{ borderRadius: 3 }}
              disabled={isFreePoll}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Billing Address
            </Typography>
            <LocationInput
              required={!isFreePoll}
              fullWidth
              label="Billing Address"
              name="payment.billingAddress"
              sx={{ borderRadius: 3 }}
              disabled={isFreePoll}
            />
          </Box>
        </Box>
      )}
      {isFreePoll && (
        <Box
          sx={{
            p: 3,
            backgroundColor: 'success.main',
            borderRadius: 3,
            display: 'flex',
            gap: 2,
          }}
        >
          <CheckCircleIcon sx={{ color: 'white' }} />
          <Typography variant="body1" color="white" fontWeight="medium">
            This job will be posted for free. No payment information required.
          </Typography>
        </Box>
      )}
    </Box>
  );
};
